import { Command } from 'commander'
import { createApiClient } from '../http.js'
import { security } from '../api/catalog.js'
import { call } from '../api/client.js'
import { managementScope } from '../context.js'
import { loadConfig } from '../config.js'
import { decodeJwtPayload } from '../jwt.js'
import { output, outputRecord } from '../output.js'

interface ScopeFlags {
  account?: string
  workspace?: string
  node?: string
  json?: boolean
}

interface CreateApiKeyResult {
  id: string
  rawKeyValue: string
  keyPrefix: string
}

/** The current user's id (sub) from the stored identity token — default API-key principal. */
async function currentUserId(): Promise<string | undefined> {
  const config = await loadConfig()
  const token = process.env.VERENTIS_TOKEN ?? config.identityToken
  return token ? (decodeJwtPayload(token)?.sub as string | undefined) : undefined
}

export function registerApiKey(program: Command): void {
  const keyCmd = program.command('apikey').description('Manage Verentis API keys (vrt_...)')

  keyCmd
    .command('create <name>')
    .description('Create an API key in the selected scope (prints the key once — store it safely)')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>', 'create a workspace-scoped key')
    .option('--node <nodeId>', 'confine the key to a node subtree (requires --workspace)')
    .option('--principal <principalId>', 'principal the key acts as (default: you)')
    .option('--principal-type <type>', 'User | Role | Service', 'User')
    .option('--expires <iso8601>', 'expiry timestamp')
    .option('--grant <scope...>', 'permission scopes to grant the key (repeatable)')
    .option('--json', 'output JSON')
    .action(async (name: string, options: ScopeFlags & { principal?: string; principalType: string; expires?: string; grant?: string[] }) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      const principalId = options.principal ?? (await currentUserId())
      if (!principalId) throw new Error('Cannot determine the key principal. Pass --principal <userId> (API-key logins have no user context).')

      const result = await call<CreateApiKeyResult>(api, security.createApiKey, {
        body: {
          ...scope,
          name,
          principalType: options.principalType,
          principalId,
          expiresAt: options.expires ?? null,
        },
      })

      for (const grantScope of options.grant ?? []) {
        await call(api, security.addApiKeyGrant, {
          params: { apiKeyId: result.id },
          body: { ...scope, apiKeyId: result.id, permissionKey: grantScope, effect: 'Allow' },
        })
      }

      if (options.json) {
        outputRecord(result as unknown as Record<string, unknown>, options)
        return
      }
      console.log(`API key '${name}' created (${result.id}).`)
      console.log(`\n  ${result.rawKeyValue}\n`)
      console.log('This value is shown ONCE. Store it safely (e.g. `verentis login --api-key ...` on the target machine).')
      if (options.grant?.length) console.log(`Granted: ${options.grant.join(', ')}`)
    })

  keyCmd
    .command('list')
    .description('List API keys in the selected scope')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .option('--search <text>')
    .option('--revoked', 'include only revoked keys')
    .option('--json', 'output JSON')
    .action(async (options: ScopeFlags & { search?: string; revoked?: boolean }) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      const result = await call<Record<string, unknown>[]>(api, security.listApiKeys, {
        query: { ...scope, search: options.search, isRevoked: options.revoked },
      })
      output(result ?? [], { name: 'Name', keyPrefix: 'Prefix', isRevoked: 'Revoked', expiresAt: 'Expires', id: 'Id' }, options, result)
    })

  keyCmd
    .command('get <apiKeyId>')
    .description('Show API key details (metadata only — the secret is never retrievable)')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .option('--json', 'output JSON')
    .action(async (apiKeyId: string, options: ScopeFlags) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      const result = await call<Record<string, unknown>>(api, security.getApiKey, { params: { apiKeyId }, query: scope })
      outputRecord(result, options)
    })

  keyCmd
    .command('update <apiKeyId>')
    .description('Update API key metadata')
    .requiredOption('--name <name>', 'new key name')
    .option('--expires <iso8601>', 'new expiry timestamp')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .action(async (apiKeyId: string, options: ScopeFlags & { name: string; expires?: string }) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      await call(api, security.updateApiKey, {
        params: { apiKeyId },
        body: { ...scope, apiKeyId, name: options.name, expiresAt: options.expires ?? null },
      })
      console.log(`API key ${apiKeyId} updated.`)
    })

  keyCmd
    .command('revoke <apiKeyId>')
    .description('Revoke an API key permanently')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .action(async (apiKeyId: string, options: ScopeFlags) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      await call(api, security.revokeApiKey, { params: { apiKeyId }, body: { ...scope, apiKeyId } })
      console.log(`API key ${apiKeyId} revoked.`)
    })

  keyCmd
    .command('regenerate <apiKeyId>')
    .description('Regenerate the secret for an API key (prints the new value once)')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .option('--json', 'output JSON')
    .action(async (apiKeyId: string, options: ScopeFlags) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      const result = await call<CreateApiKeyResult>(api, security.regenerateApiKey, { params: { apiKeyId }, body: { ...scope, apiKeyId } })
      if (options.json) {
        outputRecord(result as unknown as Record<string, unknown>, options)
        return
      }
      console.log(`API key ${apiKeyId} regenerated:`)
      console.log(`\n  ${result.rawKeyValue}\n`)
      console.log('This value is shown ONCE. The previous secret no longer works.')
    })

  const grantCmd = keyCmd.command('grant').description('Manage permission grants on an API key')

  grantCmd
    .command('add <apiKeyId> <scope>')
    .description('Grant a permission scope to an API key (e.g. node.file.read)')
    .option('--deny', 'add as a Deny grant instead of Allow')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .action(async (apiKeyId: string, scopeName: string, options: ScopeFlags & { deny?: boolean }) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      await call(api, security.addApiKeyGrant, {
        params: { apiKeyId },
        body: { ...scope, apiKeyId, permissionKey: scopeName, effect: options.deny ? 'Deny' : 'Allow' },
      })
      console.log(`Granted ${scopeName} to API key ${apiKeyId}${options.deny ? ' (Deny)' : ''}.`)
    })

  grantCmd
    .command('remove <apiKeyId> <scope>')
    .description('Remove a permission grant from an API key')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .action(async (apiKeyId: string, scopeName: string, options: ScopeFlags) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      await call(api, security.removeApiKeyGrant, { params: { apiKeyId, permissionKey: scopeName }, query: scope })
      console.log(`Removed ${scopeName} from API key ${apiKeyId}.`)
    })
}
